import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
  CustomFieldExtensionSchema,
} from '@backstage/plugin-scaffolder-react';
import { FormControl, FormHelperText, TextField } from '@material-ui/core';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';

export const Nobl9PfgOwnerFieldSchema: CustomFieldExtensionSchema = {
  returnValue: { type: 'string' },
};

interface Props extends FieldExtensionComponentProps<string> {}

/**
 * Custom scaffolder field that manages the pfg-owner label value for a Nobl9 SLO.
 *
 * - If the currently selected Nobl9 project already has a `pfg-owner` label, the field
 *   auto-fills with that value and the user may override it if needed.
 * - If the selected project has no `pfg-owner` label (value is empty string), the field
 *   is left blank and the user must supply one (enforced via the `required` schema entry
 *   in the template).
 * - The selected project's pfg-owner is communicated via the window global
 *   `__nobl9SelectedProjectPfgOwner`, set by `Nobl9ProjectFieldExtension` on project change.
 */
export const Nobl9PfgOwnerFieldComponent = ({
  onChange,
  formData,
  rawErrors,
}: Props) => {
  const projectPfgOwner = (window as any).__nobl9SelectedProjectPfgOwner as
    | string
    | undefined;

  const [value, setValue] = useState<string>(formData || '');

  // Track the last projectPfgOwner we acted on so we only update when the
  // project selection actually changes (not on every re-render).
  const prevProjectPfgOwner = useRef<string | undefined>(
    // Initialize to the current value so we don't override formData on mount.
    projectPfgOwner,
  );

  // Run when project owner value changes. Updates the field value for the selected project.
  useEffect(() => {
    if (projectPfgOwner === prevProjectPfgOwner.current) return;
    prevProjectPfgOwner.current = projectPfgOwner;

    if (projectPfgOwner !== undefined) {
      // projectPfgOwner === '' means the project exists but has no pfg-owner label.
      // projectPfgOwner === 'DL...' means auto-fill from the project label.
      setValue(projectPfgOwner);
      onChange(projectPfgOwner);
    }
  }, [projectPfgOwner, onChange]);

  const hasError = !!rawErrors?.length;
  const projectHasPfgOwner = !!projectPfgOwner;
  const projectSelected = projectPfgOwner !== undefined;

  const helperText = useMemo(() => {
    if (hasError) return rawErrors![0];
    if (!projectSelected) return 'Select a Nobl9 Project above first.';
    if (projectHasPfgOwner)
      return "Auto-filled from the selected project's pfg-owner label. You may change this if needed.";
    return "Required – the selected project has no pfg-owner label. Enter your team's distribution list email.";
  }, [hasError, projectSelected, projectHasPfgOwner, rawErrors]);

  return (
    <FormControl fullWidth margin="dense" error={hasError}>
      <TextField
        label="Team Distribution List (pfg-owner)"
        placeholder="e.g., DLISRELIABILITYENGINEERING@exchange.principal.com"
        value={value}
        disabled={!projectSelected}
        onChange={e => {
          const v = e.target.value;
          setValue(v);
          onChange(v);
        }}
        error={hasError}
        type="email"
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

export const Nobl9PfgOwnerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'Nobl9_PfgOwnerField',
    component: Nobl9PfgOwnerFieldComponent,
    schema: Nobl9PfgOwnerFieldSchema,
  }),
);

export default Nobl9PfgOwnerFieldExtension;
